import type { IntercomEventName, IntercomEvents } from './events';

import { Intercom, shutdown, trackEvent } from '@intercom/messenger-js-sdk';

const INTERCOM_APP_ID = 'd6v7fnmr';

type IntercomBootParams = {
  jwt: string;
  name: string;
  email: string;
  role: string;
  company: {
    id: string;
    name: string;
    plan: string;
    website: string;
  };
};

type TrackArgs<E extends IntercomEventName> =
  IntercomEvents[E] extends undefined
    ? [event: E]
    : [event: E, metadata: IntercomEvents[E]];

type IntercomStatus = 'waiting' | 'booted' | 'disabled';

// The SDK drops events sent before boot, so they wait for it unless Intercom is disabled.
let status: IntercomStatus = 'waiting';
let pendingEvents: (() => void)[] = [];

const send = (sendEvent: () => void) => {
  try {
    sendEvent();
  } catch {
    // Tracking must never break the user action that triggered it.
  }
};

export const bootIntercom = ({
  jwt,
  name,
  email,
  role,
  company,
}: IntercomBootParams) => {
  Intercom({
    app_id: INTERCOM_APP_ID,
    intercom_user_jwt: jwt,
    name,
    email,
    role,
    company: {
      company_id: company.id,
      name: company.name,
      plan: company.plan,
      website: company.website,
    },
  });
  status = 'booted';
  for (const sendEvent of pendingEvents) {
    send(sendEvent);
  }
  pendingEvents = [];
};

export const disableIntercom = () => {
  status = 'disabled';
  pendingEvents = [];
};

export const shutdownIntercom = () => {
  // The SDK warns when shut down without having been booted.
  if (status === 'booted') {
    shutdown();
  }
  status = 'waiting';
  // Pending events belong to the user who is leaving.
  pendingEvents = [];
};

export const track = <E extends IntercomEventName>(...args: TrackArgs<E>) => {
  const sendEvent = () => trackEvent(...args);

  if (status === 'disabled') {
    return;
  }
  if (status === 'waiting') {
    pendingEvents.push(sendEvent);

    return;
  }
  send(sendEvent);
};
